import { useMutation, useQuery } from '@apollo/client';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { FundClosingDrawdownStatusEnumType, FundClosingDrawdownType } from '__generated__/graphql';
import { FC, useState } from 'react';

import Button from 'primitives/Button';

import DataTable from 'components/DataTable';
import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

import { formatCurrency } from 'utils/format-helper';

const GET_FUND_CLOSING_DETAILS_QUERY = gql(`
  query getFundClosingDetails($id: ID!) {
    fundClosing(id: $id) {
      id
      setupFeePercent
    }
  }
`);

const CREATE_DRAWDOWN_MUTATION = gql(`
    mutation createFundClosingDrawdown($fundClosingId: ID!, $amount: Float!) {
        createFundClosingDrawdown(fundClosingId: $fundClosingId, amount: $amount, sendDrawdown: true) {
            id
            amount
            status
            createdAt
            updatedAt
        }
    }
`);

const UPDATE_DRAWDOWN_MUTATION = gql(`
    mutation updateFundClosingDrawdown($id: ID!, $fundClosingId: ID!, $amount: Float!, $status: FundClosingDrawdownStatusEnumType!) {
        updateFundClosingDrawdown(id: $id, fundClosingId: $fundClosingId, amount: $amount, status: $status) {
            id
        }
    }
`);

const SEND_DRAWDOWN_MUTATION = gql(`
    mutation sendDrawdownNoticeForFundClosing($fundClosingId: ID!, $drawdownId: ID!) {
        sendDrawdownNoticeForFundClosing(fundClosingId: $fundClosingId, drawdownId: $drawdownId)
    }
`);

const FundClosingDrawdownSection: FC<{
  data: FundClosingDrawdownType[];
  fundClosingId: string;
  refetch: () => void;
}> = ({ data, fundClosingId, refetch }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [editable, setEditable] = useState<FundClosingDrawdownType>({} as FundClosingDrawdownType);
  const { data: fundClosingDetails } = useQuery(GET_FUND_CLOSING_DETAILS_QUERY, {
    variables: { id: fundClosingId },
  });

  const [createDrawdown, { loading, error }] = useMutation(CREATE_DRAWDOWN_MUTATION);
  const [updateDrawdown, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_DRAWDOWN_MUTATION);

  const [sendDrawdown, { loading: sendDrawdownLoading }] = useMutation(SEND_DRAWDOWN_MUTATION);

  const [setupFee, setSetupFee] = useState<number>(0);

  return (
    <div>
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-medium">Drawdown History</h2>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setShowCreate(true);
            }}
          >
            Add Drawdown
          </Button>
        </div>
      </div>
      {showCreate && (
        <div className="my-4 bg-gray-100 p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <h3 className="font-medium mb-3">Send Drawdown</h3>
            <Button variant="text" onClick={() => setShowCreate(false)}>
              Close
            </Button>
          </div>
          <FormPanel
            loading={loading}
            error={error}
            onSubmit={data => {
              createDrawdown({
                variables: {
                  fundClosingId: fundClosingId as string,
                  amount: data.amount,
                },
              }).then(() => {
                setShowCreate(false);
                refetch();
              });
            }}
            submitButtonLabel="Send Drawdown"
          >
            <FormInput
              onChange={value =>
                setSetupFee(
                  value.amount *
                    Number((fundClosingDetails?.fundClosing?.setupFeePercent || 2) / 100)
                )
              }
              type="number"
              fieldName="amount"
              label="Amount"
              defaultValue={0}
            />
            {/* <FormInput
              type="select"
              fieldName="status"
              label="Status"
              defaultValue={FundClosingDrawdownStatusEnumType.Pending}
              readOnlyMode={true}
              editable={false}
              options={Object.values(FundClosingDrawdownStatusEnumType).map(status => ({
                label: status,
                value: status,
              }))}
            /> */}
            <p className="text-sm text-gray-500 min-w-max">
              Setup Fee is: {formatCurrency(setupFee)}
            </p>
          </FormPanel>
        </div>
      )}

      <DataTable
        data={data}
        onClick={value => {
          setEditable(value);
        }}
        columns={[
          {
            label: 'Status',
            fieldName: 'status',
            type: 'PAYMENT_STATUS',
          },
          {
            label: 'Amount',
            fieldName: 'amount',
            type: 'CURRENCY',
          },

          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATE',
          },
          {
            label: 'Updated At',
            fieldName: 'updatedAt',
            type: 'DATE',
          },
          {
            label: 'EDIT',
            type: 'CUSTOM',
            fieldName: 'edit',
            customValue: () => <Button variant="text">Edit</Button>,
          },
        ]}
      />

      {Object.keys(editable).length > 0 && (
        <div className="bg-gray-100 p-4 rounded-lg mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">Edit Drawdown</h3>
            <Button
              variant="text"
              onClick={() => {
                setEditable({} as FundClosingDrawdownType);
              }}
            >
              Close
            </Button>
          </div>
          <FormPanelWithReadMode
            key={editable.id}
            loading={updateLoading}
            error={updateError}
            onSubmit={data => {
              updateDrawdown({
                variables: {
                  id: editable.id,
                  fundClosingId: fundClosingId as string,
                  amount: data.amount,
                  status: data.status,
                },
              }).then(() => {
                refetch();
              });
            }}
          >
            <FormInput
              type="number"
              fieldName="amount"
              label="Amount"
              defaultValue={editable.amount}
            />
            <FormInput
              type="select"
              fieldName="status"
              label="Status"
              defaultValue={editable.status}
              options={Object.values(FundClosingDrawdownStatusEnumType).map(status => ({
                label: status,
                value: status,
              }))}
            />
            <FormInput
              type="date"
              fieldName="createdAt"
              label="Created At"
              defaultValue={editable.createdAt}
              readOnlyMode={true}
              editable={false}
            />
            <FormInput
              type="date"
              fieldName="updatedAt"
              label="Updated At"
              defaultValue={editable.updatedAt}
              readOnlyMode={true}
              editable={false}
            />
            <Button
              className="w-40 flex items-center justify-center gap-2"
              loading={sendDrawdownLoading}
              disabled={sendDrawdownLoading}
              onClick={() => {
                sendDrawdown({
                  variables: {
                    fundClosingId: fundClosingId as string,
                    drawdownId: editable.id,
                  },
                });
              }}
            >
              Send Drawdown
              <DocumentIcon className="w-4 h-4 -mt-1" />
            </Button>
          </FormPanelWithReadMode>
        </div>
      )}
    </div>
  );
};

export default FundClosingDrawdownSection;
